import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  LifeBuoy, 
  ShieldAlert, 
  ArrowRight, 
  PhoneCall, 
  CheckCircle2, 
  HelpCircle, 
  FileText, 
  CreditCard, 
  Lock, 
  Mail, 
  Users, 
  Building2, 
  ExternalLink,
  Sparkles
} from 'lucide-react';
import { useStore } from '../store/useStore';
import { startNewEmergencyCase } from '../api/recovery';
import { RecoveryCase } from '../types';

export const IdentityTheftPage: React.FC = () => {
  const navigate = useNavigate();
  const { recoveryCase, openAiModal } = useStore();
  const [selectedIncident, setSelectedIncident] = useState<RecoveryCase['incidentType']>('id_stolen');
  const [customDetails, setCustomDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const incidentOptions: {
    id: RecoveryCase['incidentType'];
    title: string;
    description: string;
    icon: any;
  }[] = [
    {
      id: 'id_stolen',
      title: 'My ID was stolen or lost',
      description: 'Physical driver licence, passport, Medicare card, or wallet missing.',
      icon: FileText
    },
    {
      id: 'bank_compromised',
      title: 'My bank account or card was compromised',
      description: 'Unauthorized bank transactions, stolen card, or fraudulent withdrawals.',
      icon: CreditCard
    },
    {
      id: 'account_opened',
      title: 'Someone opened an account in my name',
      description: 'Received unexpected phone contract, loan letter, or credit card in your name.',
      icon: Building2
    },
    {
      id: 'email_hacked',
      title: 'My email or phone was hacked / SIM swapped',
      description: 'Locked out of primary email or mobile phone suddenly lost carrier network.',
      icon: Mail
    },
    {
      id: 'data_breach',
      title: 'My personal information was exposed in a breach',
      description: 'Received notice that your data was in a corporate breach (e.g. Optus, Medibank).',
      icon: Lock
    },
    {
      id: 'impersonation',
      title: 'I think someone is impersonating me',
      description: 'Fake social media profile, rental application, or business impersonation.',
      icon: Users
    },
    {
      id: 'unknown',
      title: "I don't know exactly what happened",
      description: 'Received strange letters, debt collection calls, or suspicious multi-factor prompts.',
      icon: HelpCircle
    }
  ];

  const handleStartRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await startNewEmergencyCase(selectedIncident, customDetails);
      navigate('/recovery-wizard');
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-12">
      {/* Hero Header */}
      <div className="text-center space-y-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-950 text-rose-300 text-xs font-semibold border border-rose-800">
          <LifeBuoy className="w-3.5 h-3.5" />
          <span>Identity Theft Emergency Response Centre</span>
        </div>
        <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">
          Think someone has stolen your identity?
        </h1>
        <p className="text-lg text-slate-300 max-w-2xl mx-auto font-normal">
          Don't panic. We'll guide you through what to do next with an official step-by-step Australian recovery plan.
        </p>
      </div>

      {/* Immediate Emergency Contacts Strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 flex items-center gap-3.5">
          <div className="p-3 rounded-xl bg-emerald-950 border border-emerald-800 text-emerald-400">
            <PhoneCall className="w-5 h-5" />
          </div>
          <div>
            <div className="font-bold text-white text-sm">IDCARE National Support</div>
            <div className="text-xs text-emerald-400 font-mono font-bold">1800 595 160 (Free)</div>
            <div className="text-[10px] text-slate-400">Official Australian ID Charity</div>
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 flex items-center gap-3.5">
          <div className="p-3 rounded-xl bg-blue-950 border border-blue-800 text-blue-400">
            <ShieldAlert className="w-5 h-5" />
          </div>
          <div>
            <div className="font-bold text-white text-sm">ReportCyber (Police)</div>
            <div className="text-xs text-blue-400 font-mono">cyber.gov.au</div>
            <div className="text-[10px] text-slate-400">Lodge Official Incident Report</div>
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 flex items-center gap-3.5">
          <div className="p-3 rounded-xl bg-indigo-950 border border-indigo-800 text-indigo-400">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <div className="font-bold text-white text-sm">AI Emergency Assistant</div>
            <button
              onClick={openAiModal}
              className="text-xs text-indigo-400 hover:underline font-semibold"
            >
              Ask Instant Questions →
            </button>
            <div className="text-[10px] text-slate-400">Approved Australian Advice</div>
          </div>
        </div>
      </div>

      {/* Emergency Questionnaire */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-10 shadow-2xl space-y-8">
        <div className="border-b border-slate-800 pb-4">
          <h2 className="text-xl font-bold text-white">Emergency Questionnaire: What happened?</h2>
          <p className="text-xs text-slate-400 mt-1">Select the option that best describes your situation to generate a tailored recovery checklist.</p>
        </div>

        <form onSubmit={handleStartRecovery} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {incidentOptions.map((opt) => {
              const Icon = opt.icon;
              const isSelected = selectedIncident === opt.id;
              return (
                <div
                  key={opt.id}
                  onClick={() => setSelectedIncident(opt.id)}
                  className={`p-4 rounded-2xl border cursor-pointer transition-all flex items-start gap-3.5 ${
                    isSelected
                      ? 'bg-rose-950/40 border-rose-600 text-white shadow-lg'
                      : 'bg-slate-950/60 border-slate-800 hover:border-slate-700 text-slate-300'
                  }`}
                >
                  <div className={`p-2.5 rounded-xl ${isSelected ? 'bg-rose-900 text-rose-200' : 'bg-slate-900 text-slate-400'}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="space-y-1 flex-1">
                    <div className="text-sm font-bold text-white flex items-center justify-between">
                      <span>{opt.title}</span>
                      {isSelected && <CheckCircle2 className="w-4 h-4 text-rose-400" />}
                    </div>
                    <p className="text-xs text-slate-400 leading-snug">{opt.description}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="space-y-2">
            <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider">
              Additional Details (Optional notes for your case record):
            </label>
            <textarea
              rows={3}
              value={customDetails}
              onChange={(e) => setCustomDetails(e.target.value)}
              placeholder="e.g. Stole wallet on train containing NSW driver licence and debit card. Bank already notified to cancel card."
              className="w-full p-3.5 rounded-xl bg-slate-950 border border-slate-700 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-rose-500"
            />
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-slate-800">
            <div className="text-xs text-slate-400 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              <span>Generates an 8-stage interactive recovery checklist with direct Australian government contacts.</span>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-bold text-sm shadow-xl shadow-rose-950/50 transition-colors flex items-center justify-center gap-2 flex-shrink-0"
            >
              {isSubmitting ? (
                <span>Generating Roadmap...</span>
              ) : (
                <>
                  <span>Start Emergency Response</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Existing Active Plan Shortcut */}
      {recoveryCase && (
        <div className="p-6 rounded-2xl bg-slate-900 border border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="space-y-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Existing Case on File</span>
            <h4 className="text-base font-bold text-white">{recoveryCase.incidentTitle}</h4>
            <p className="text-xs text-slate-400">
              Progress: {recoveryCase.completedStepsCount} of {recoveryCase.totalStepsCount} steps completed ({recoveryCase.progressPercentage}%)
            </p>
          </div>
          <Link
            to="/recovery-wizard"
            className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold transition-colors flex items-center gap-1.5"
          >
            <span>Open Current 8-Step Wizard</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}
    </div>
  );
};
